import { useMemo, useState } from 'react';
import { ChevronDownIcon, ChevronRightIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { JournalEntryBody } from '../collaborations/CollaborationJournalViewer';
import { useJournalEntryDetail } from '../../hooks/useRunExplorer';
import type { CollaborationJournal } from '../../types';
import type { Turn } from '../../types/runExplorer';
import {
  entryStepId,
  isProblemEntry,
  isSkippedEntry,
  outputCheckOf,
  schemaCheckOf,
  tokenCount,
} from '../../utils/runTurnModel';
import { formatClock, formatMs } from './runExplorerShared';

type JournalView = 'by-step' | 'chronological' | 'problems';

const EVENT_LABELS: Record<string, string> = {
  llm_call: 'Model call',
  tool_call: 'Tool call',
  completed: 'completed',
  failed: 'failed',
  waiting: 'waiting',
  skipped: 'skipped',
};

/** One line saying what an entry is, for the collapsed row. */
function describeEntry(entry: CollaborationJournal): string {
  if (isSkippedEntry(entry)) {
    const branch = entry.branchStepId ?? entry.metadata?.branchStepId;
    return `skipped${branch ? ` by ${String(branch)}` : ''}`;
  }
  const parts: string[] = [EVENT_LABELS[entry.event] ?? entry.event];
  if (entry.event === 'llm_call') {
    if (entry.llmModel) parts.push(entry.llmModel);
    const finish = entry.metadata?.finishReason;
    if (typeof finish === 'string') parts.push(finish);
    const tokens = tokenCount(entry.usage, 'inputTokens') + tokenCount(entry.usage, 'outputTokens');
    if (tokens > 0) parts.push(`${tokens.toLocaleString()} tok`);
  }
  if (entry.event === 'tool_call' && entry.functionName) parts.push(entry.functionName);
  if (entry.event === 'tool_call' && entry.error) parts.push('error');
  const schema = schemaCheckOf(entry);
  if (schema) parts.push(schema.passed ? 'output matches schema' : 'output does not match schema');
  const outputCheck = outputCheckOf(entry);
  if (outputCheck && !outputCheck.passed) parts.push('reply failed the output check');
  return parts.join(' · ');
}

function JournalRow({ collaborationId, entry, turnLabel }: { collaborationId: string; entry: CollaborationJournal; turnLabel?: string }) {
  const [open, setOpen] = useState(false);
  const { data, isLoading } = useJournalEntryDetail(collaborationId, entry.id, open && !isSkippedEntry(entry));
  const problem = isProblemEntry(entry);
  const merged = data ? { ...entry, ...data } : entry;

  return (
    <li data-testid={`journal-row-${entry.id}`} className={`rounded border ${problem ? 'border-amber-300 dark:border-amber-700' : 'border-gray-200 dark:border-gray-700'}`}>
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-2 px-3 py-1.5 text-left text-xs hover:bg-gray-50 dark:hover:bg-gray-800"
      >
        {open ? <ChevronDownIcon className="h-3.5 w-3.5 text-gray-400" /> : <ChevronRightIcon className="h-3.5 w-3.5 text-gray-400" />}
        <span className="w-24 flex-shrink-0 font-mono text-gray-500 dark:text-gray-400">{formatClock(entry.createdAt, true)}</span>
        {turnLabel && <span className="flex-shrink-0 text-gray-500 dark:text-gray-400">{turnLabel}</span>}
        <span className="w-40 flex-shrink-0 truncate font-mono font-medium text-gray-800 dark:text-gray-100">{entryStepId(entry)}</span>
        <span className="flex-1 min-w-0 truncate text-gray-700 dark:text-gray-200">{describeEntry(entry)}</span>
        {problem && <ExclamationTriangleIcon className="h-3.5 w-3.5 text-amber-500 flex-shrink-0" aria-label="problem" />}
        <span className="w-16 flex-shrink-0 text-right text-gray-500 dark:text-gray-400">{entry.duration != null ? formatMs(entry.duration) : ''}</span>
      </button>
      {open && (
        <div className="border-t border-gray-100 dark:border-gray-700">
          {isLoading ? (
            <p className="px-4 py-2 text-xs text-gray-500 dark:text-gray-400">Loading…</p>
          ) : (
            <div className="pt-3">
              <JournalEntryBody journal={merged} />
            </div>
          )}
        </div>
      )}
    </li>
  );
}

/**
 * The run's journal (task 1151, step 10), loaded whole — no paging. By Step
 * groups the selected turn's entries under their step in path order;
 * Chronological lists the selected turn in time order; Problems Only lists,
 * across every turn, the failures, failed schema checks, steps skipped
 * because an earlier step was skipped (a routine branch skip is not listed,
 * task 1162), replies that failed the output check (task 1139) and tool calls
 * that returned an error.
 * Opening an entry fetches its large fields.
 */
export function RunJournal({
  collaborationId,
  turns,
  selectedTurnIndex,
}: {
  collaborationId: string;
  turns: Turn[];
  selectedTurnIndex: number;
}) {
  const [view, setView] = useState<JournalView>('by-step');
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const turn = turns.find((t) => t.index === selectedTurnIndex) ?? null;
  const multiTurn = turns.length > 1;

  const problems = useMemo(
    () => turns.flatMap((t) => t.entries.filter(isProblemEntry).map((entry) => ({ entry, turn: t.index }))),
    [turns]
  );

  const views: { id: JournalView; label: string }[] = [
    { id: 'by-step', label: 'By step' },
    { id: 'chronological', label: 'Chronological' },
    { id: 'problems', label: `Problems only (${problems.length})` },
  ];

  return (
    <div data-testid="run-journal">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <h2 className="text-sm font-semibold text-gray-900 dark:text-white">
          Journal{view !== 'problems' && multiTurn ? `, turn ${selectedTurnIndex + 1}` : ''}
        </h2>
        <div className="inline-flex rounded-md border border-gray-300 dark:border-gray-600 overflow-hidden text-xs" role="group" aria-label="Journal view">
          {views.map((v) => (
            <button
              key={v.id}
              type="button"
              aria-pressed={view === v.id}
              onClick={() => setView(v.id)}
              className={`px-2.5 py-1 ${view === v.id ? 'bg-indigo-600 text-white' : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200'}`}
            >
              {v.label}
            </button>
          ))}
        </div>
      </div>

      {view === 'by-step' && turn && (
        <div className="space-y-2">
          {turn.path.map((stepId) => {
            const step = turn.steps[stepId];
            const isCollapsed = collapsed[stepId] ?? false;
            return (
              <div key={stepId} data-testid={`journal-group-${stepId}`}>
                <button
                  type="button"
                  aria-expanded={!isCollapsed}
                  onClick={() => setCollapsed({ ...collapsed, [stepId]: !isCollapsed })}
                  className="flex items-center gap-1 text-xs font-semibold font-mono text-gray-700 dark:text-gray-200 mb-1"
                >
                  {isCollapsed ? <ChevronRightIcon className="h-3.5 w-3.5" /> : <ChevronDownIcon className="h-3.5 w-3.5" />}
                  {stepId}
                  <span className="font-normal text-gray-500 dark:text-gray-400">
                    · {step.entries.length} {step.entries.length === 1 ? 'entry' : 'entries'}
                  </span>
                </button>
                {!isCollapsed && (
                  <ul className="space-y-1 pl-4">
                    {step.entries.map((entry) => (
                      <JournalRow key={entry.id} collaborationId={collaborationId} entry={entry} />
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
          {turn.skipped.length > 0 && (
            <p className="text-xs text-gray-500 dark:text-gray-400" data-testid="journal-skipped-summary">
              {turn.skipped.length} {turn.skipped.length === 1 ? 'step' : 'steps'} skipped by routing: {turn.skipped.join(', ')}
            </p>
          )}
          {turn.path.length === 0 && <p className="text-sm text-gray-500 dark:text-gray-400">No entries in this turn yet.</p>}
        </div>
      )}

      {view === 'chronological' && turn && (
        <ul className="space-y-1">
          {turn.entries.map((entry) => (
            <JournalRow key={entry.id} collaborationId={collaborationId} entry={entry} />
          ))}
        </ul>
      )}

      {view === 'problems' &&
        (problems.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400" data-testid="journal-no-problems">
            No problems recorded in this run.
          </p>
        ) : (
          <ul className="space-y-1">
            {problems.map(({ entry, turn: t }) => (
              <JournalRow
                key={entry.id}
                collaborationId={collaborationId}
                entry={entry}
                turnLabel={multiTurn ? `Turn ${t + 1}` : undefined}
              />
            ))}
          </ul>
        ))}
    </div>
  );
}
